import { useState, useEffect, useCallback } from 'react';
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { useJsApiLoader } from '@react-google-maps/api';
import { getMapData } from '../../apis/personnelApi';
import axiosInstance from '../../utils/axiosConfig'; // Fix import path
import MapModal from './../modals/MapModal';

interface MapSiteData {
  siteID: number;
  siteNumber: number;
  siteName: string;
  address1?: string;
  address2?: string;
  address3?: string;
  phone?: string;
  county?: string;
  principal?: string;
  latitude?: number;
  longitude?: number;
  attendanceCount: number;
  totalPersonnelCount: number;
}

interface SitePersonnelDetail {
  siteID: number;
  siteNumber: number;
  siteName: string;
  address1?: string;
  phone?: string;
  county?: string;
  principal?: string;
  latitude?: number;
  longitude?: number;
  personnel: Array<{
    personalID: number;
    firstName: string;
    lastName: string;
    position?: string;
    timeIn?: string;
    timeOut?: string;
    lunchIn?: string;
    lunchOut?: string;
  }>;
}

const Map = () => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyDdaDcxALVE_me0JlT63C83edrlMI7wPas', // Make sure to add HTTP referrers for restricting the unauthorized usage, Don't add the localhost as the referrers permanently
  });

  const [hoveredMarker, setHoveredMarker] = useState<any>(null);
  const [selectedMarker, setSelectedMarker] = useState<any>(null);
  const [markers, setMarkers] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSiteData, setSelectedSiteData] = useState<MapSiteData | null>(null);
  const [personnelDetails, setPersonnelDetails] = useState<SitePersonnelDetail | null>(null);
  const [loadingPersonnel, setLoadingPersonnel] = useState(false);

  const storedCenter = JSON.parse(localStorage.getItem('mapCenter') || '{}');
  const [center, setCenter] = useState(
    storedCenter.lat && storedCenter.lng
      ? { lat: storedCenter.lat, lng: storedCenter.lng }
      : { lat: 40.7589, lng: -73.0514 } // Long Island center coordinates
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getMapData();
        const newMarkerData = generateMarkerData(data);
        setMarkers(newMarkerData);
      } catch (error) {
        console.error('Error fetching map data:', error);
      }
    };

    fetchData();
  }, []);

  const generateMarkerData = (mapData: MapSiteData[]) => {
    return mapData.map((siteData: MapSiteData, i: number) => {
      return {
        id: i,
        position: { lat: siteData.latitude, lng: siteData.longitude },
        name: `${siteData.siteNumber} ${siteData.siteName}`,
        description: `Today's Attendance: ${siteData.attendanceCount}/${siteData.totalPersonnelCount} staff`,
        siteData: siteData
      };
    }).filter(marker => marker.position.lat && marker.position.lng); // Filter out markers without coordinates
  };

  const fetchPersonnelDetails = async (siteId: number) => {
    setLoadingPersonnel(true);
    try {
      const currentDate = new Date().toISOString().split('T')[0];
      const response = await axiosInstance.get(`/Personel/site_personnel_details?siteId=${siteId}&date=${currentDate}`);
      if (response.data.success) {
        setPersonnelDetails(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching personnel details:', error);
    } finally {
      setLoadingPersonnel(false);
    }
  };

  const handleMarkerClick = (marker: typeof markers[0]) => {
    setSelectedMarker(marker);
    setSelectedSiteData(marker.siteData);
    setPersonnelDetails(null); // Reset personnel details
    fetchPersonnelDetails(marker.siteData.siteID);
    setIsModalOpen(true);
  };

  const handleHover = useCallback((marker: typeof markers[0]) => {
    setHoveredMarker(marker);
  }, []);

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMarker(null);
    setSelectedSiteData(null);
    setPersonnelDetails(null);
  };

  if (!isLoaded) {
    return <div>Loading...</div>;
  }

  return (
    <GoogleMap
      mapContainerStyle={{ height: '100%', width: '100%', borderRadius: '32px', minHeight: '350px' }}
      center={center}
      zoom={10} // Zoomed in to better show Long Island area
    >
      {markers.map((marker) => (
        <Marker
          key={marker.id}
          position={marker.position}
          icon={{
            url: hoveredMarker && hoveredMarker.id === marker.id
              ? require('./../../assets/images/marker.png')
              : require('./../../assets/images/darkMarker.png'),
            fillColor: '#EB00FF',
            scale: 7,
          }}
          onMouseOver={() => handleHover(marker)}
          onMouseOut={() => setHoveredMarker(null)}
          onClick={() => handleMarkerClick(marker)}
        />
      ))}

      {hoveredMarker && (
        <InfoWindow
          position={hoveredMarker.position}
          onCloseClick={() => setHoveredMarker(null)}
        >
          <div onClick={() => handleMarkerClick(hoveredMarker)} className="mapInfo" style={{ cursor: 'pointer' }}>
            <h3>{hoveredMarker.name}</h3>
            <p>{hoveredMarker.description}</p>
          </div>
        </InfoWindow>
      )}

      {isModalOpen && (
        <MapModal
          isOpen={isModalOpen}
          closeModal={closeModal}
          selectedSiteData={selectedSiteData}
          personnelDetails={personnelDetails}
        />
      )}
    </GoogleMap>
  );
};

export default Map;
